import React, { useEffect, useMemo, useState } from "react";
import { usePreference } from "../contexts/PreferencesContext";
import ReactECharts from "echarts-for-react";
import useApi from "../useApi";
import { getApiPaths } from "../constants/healthEndpoints";
import { getHealthApiBase } from "../constants/healthApi";
import { axisDefaults, chartDefaults, useChartTheme } from "../charts/theme";

/** Market code per account, for the series label. */
/** Only these three markets answer today; US, DE, AT and AU 404. */
const LIVE_MARKETS = [
  { code: "CH", path: "CH" },
  { code: "PT", path: "PT" },
  { code: "UK", path: "UK" },
];

const MARKET_COLORS = {
  UK: "#3ED2F0",
  PT: "#11A600",
  CH: "#FFC400",
  DE: "#F0466F",
};

/** Market code per account, for the series label. */
const MARKET_BY_ACCOUNT = {
  3547573: "UK",
  4091918: "PT",
  3592556: "CH",
  3497361: "DE",
};

/**
 * Ranges the trend chart offers.
 *
 * Bounded by the endpoint, not by preference: MarketStoreHealth returns a fixed
 * 30 daily points, so a week and a month are slices of what already arrives.
 * 90 days and an intraday "today" both need the API to serve them first.
 */
const RANGES = [
  { key: "week", label: "Last week", days: 7 },
  { key: "month", label: "Last month", days: 30 },
];

/**
 * Daily service health for every live market, fetched in parallel.
 *
 * One request per market rather than a single global endpoint, because none
 * exists: history is per market. Wall time is the slowest market, not their
 * sum. Markets that fail are dropped rather than failing the chart — a missing
 * line is better than no chart.
 */
function useGlobalTrends(enabled, days) {
  const [trends, setTrends] = useState([]);

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    const healthApiBase = getHealthApiBase();

    Promise.all(
      LIVE_MARKETS.map(async ({ code, path }) => {
        try {
          const response = await fetch(
            `${healthApiBase}/${path}/MarketServiceHealth`,
            { signal: controller.signal }
          );
          if (!response.ok) return { code, points: [] };
          const rows = await response.json();
          return { code, points: Array.isArray(rows) ? rows : [] };
        } catch {
          return { code, points: [] };
        }
      })
    ).then((all) => {
      if (controller.signal.aborted) return;
      // Sorted before slicing: the API returns newest first, so taking the
      // last N of the raw array would quietly show the oldest window.
      setTrends(
        all.map(({ code, points }) => ({
          code,
          points: [...points]
            .filter((d) => d && d.timestamp != null)
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
            .slice(-days),
        }))
      );
    });

    return () => controller.abort();
  }, [enabled, days]);

  return trends;
}

const TrendChart = ({ mode, accountId }) => {
  const theme = useChartTheme();
  // Remembered per user, like the markets panel view.
  const [rangeKey, setRangeKey] = usePreference("trendRange", "month");
  const range = RANGES.find((r) => r.key === rangeKey) || RANGES[1];
  const globalTrends = useGlobalTrends(mode === 'global', range.days);

  // Fetch data based on mode
  const apiPaths = useMemo(() => {
    return mode === 'market' ? getApiPaths(accountId) : null;
  }, [mode, accountId]);

  const { data: trendData, loading } = useApi(apiPaths?.TREND_VIEW);

  const option = useMemo(() => {
    const base = chartDefaults(theme);
    const axis = axisDefaults(theme);

    if (mode === 'global') {
      // Global view: one line per live market, from each market's own daily
      // service health. No weighting needed — the chart compares markets, it
      // does not aggregate them.
      const anyPoints = globalTrends.some((m) => m.points.length);
      const axisDates = globalTrends.reduce(
        (longest, m) => (m.points.length > longest.length ? m.points : longest),
        []
      );

      return {
        ...base,
        tooltip: { ...base.tooltip, trigger: "axis" },
        legend: { ...base.legend, bottom: 0 },
        xAxis: {
          ...axis,
          type: "category",
          data: axisDates.map((d) =>
            new Date(d.timestamp).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
            })
          ),
        },
        yAxis: {
          ...axis,
          type: "value",
          min: 0,
          max: 100,
          axisLabel: { ...axis.axisLabel, formatter: "{value}%" },
        },
        series: anyPoints
          ? globalTrends.map((m) => ({
              name: m.code,
              type: "line",
              smooth: true,
              data: m.points.map((d) => d.value),
              lineStyle: { width: 2 },
              itemStyle: { color: MARKET_COLORS[m.code] },
            }))
          : [],
      };
    } else {
      // Market view: real daily history from MarketStoreHealth.
      //
      // The API returns newest first, so it is sorted before slicing — taking
      // the "last 7" of an unsorted array would have given the oldest week
      // while looking entirely plausible.
      const points = Array.isArray(trendData)
        ? [...trendData]
            .filter((d) => d && d.timestamp != null)
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
            .slice(-range.days)
        : [];

      return {
        ...base,
        tooltip: { ...base.tooltip, trigger: "axis" },
        legend: { ...base.legend, bottom: 0 },
        xAxis: {
          ...axis,
          type: "category",
          data: points.map((d) =>
            new Date(d.timestamp).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
            })
          ),
        },
        yAxis: {
          ...axis,
          type: "value",
          min: 0,
          max: 100,
          axisLabel: { ...axis.axisLabel, formatter: "{value}%" },
        },
        series: [
          {
            name: MARKET_BY_ACCOUNT[accountId] || "Market",
            type: "line",
            smooth: true,
            data: points.map((d) => d.value),
            lineStyle: { width: 3 },
            itemStyle: { color: "#2563eb" },
          },
        ],
      };
    }
  }, [mode, accountId, trendData, theme, range.days, globalTrends]);


  return (
    <div className="global-card">
      <div className="global-card-header">
        <h2>Health Trend ({mode === 'global' ? 'Global' : 'Market'})</h2>

        {(
          <div className="trend-range" role="group" aria-label="Trend range">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`trend-range__btn${
                  r.key === rangeKey ? ' is-active' : ''
                }`}
                aria-pressed={r.key === rangeKey}
                onClick={() => setRangeKey(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ height: "25rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
          Loading chart...
        </div>
      ) : (
       <> <ReactECharts
          option={option}
          style={{ height: "25rem", width: "100%" }}
        />
       
        </>
      )}
    </div>
  );
};

export default TrendChart;